import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, CalendarDays, Phone, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { formatEventTime } from "./utils";

export default function OpenHouseDetailDialog({ id, onClose, onUpdated }) {
  const [oh, setOh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      setOh(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/openhouses/${id}`);
        setOh(data);
        setNotes(data.notes || "");
      } catch (e) {
        toast.error("Failed to load open house", { description: formatApiError(e) });
        onClose();
      }
    })();
  }, [id, onClose]);

  if (!id || !oh) return null;

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.patch(`/openhouses/${oh.id}`, { notes });
      toast.success("Notes saved");
      onUpdated();
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="oh-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-start gap-2 pr-8">
            <Home className="h-5 w-5 text-slate-500 mt-0.5 shrink-0" />
            <span>{oh.address_raw || "Open House"}</span>
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            MLS {oh.mls_number || "—"} · {oh.neighborhood || "Saskatoon"}
          </DialogDescription>
        </DialogHeader>

        {oh.photo_url ? (
          <img
            src={oh.photo_url}
            alt={oh.address_raw}
            className="w-full max-h-60 object-cover rounded-md border border-slate-200"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : null}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <DetailField label="Price" value={oh.price_display || "—"} />
          <DetailField label="Bedrooms" value={oh.bedrooms || "—"} />
          <DetailField label="Bathrooms" value={oh.bathrooms || "—"} />
          <DetailField label="Type" value={oh.property_type || "—"} />
        </div>

        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Open House events</div>
          <ul className="space-y-1">
            {(oh.events || []).map((ev, idx) => (
              <li key={idx} className="text-sm flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400" />
                <span className="tabular-nums">{formatEventTime(ev)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Listing salesperson</div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900">{oh.realtor_name || "Unassigned"}</span>
            {oh.realtor_phone ? (
              <a
                href={`tel:${oh.realtor_phone}`}
                className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline"
              >
                <Phone className="h-3 w-3" /> {oh.realtor_phone}
              </a>
            ) : null}
            {oh.realtor_id ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`/realtors/${oh.realtor_id}`)}
                className="ml-auto text-xs min-h-[36px]"
                data-testid="oh-open-realtor-button"
              >
                Open realtor →
              </Button>
            ) : null}
          </div>
          {oh.brokerage_name ? (
            <div className="text-xs text-slate-500 mt-1">{oh.brokerage_name}</div>
          ) : null}
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1"
            placeholder="What did you notice on the open house?"
            data-testid="oh-notes-textarea"
          />
        </div>

        {oh.listing_url ? (
          <a
            href={oh.listing_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand-strong))] hover:underline"
          >
            View on Realtor.ca <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="min-h-[44px]">
            Close
          </Button>
          <Button
            onClick={saveNotes}
            disabled={busy}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="oh-save-notes-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save notes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900 truncate">{value}</div>
    </div>
  );
}
